import { useForm } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  AuthLayout,
  AuthPanel,
  AuthPanelDescription,
  AuthPanelHeader,
  AuthPanelTitle,
} from '@/components/auth-layout';
import { FieldError } from '@/components/field-error';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/set-password')({
  component: SetPasswordPage,
});

const passwordSchema = z
  .object({
    password: z
      .string()
      .min(12, 'Use at least 12 characters.')
      .regex(/[a-z]/, 'Add a lower-case letter.')
      .regex(/[A-Z]/, 'Add a capital letter.')
      .regex(/\d/, 'Add a digit.'),
    confirm: z.string(),
  })
  .refine((value) => value.password === value.confirm, {
    message: 'The two passwords differ.',
    path: ['confirm'],
  });

function readLinkToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('type') === 'recovery' ? params.get('token_hash') : null;
}

/** Where a staff invite or sign-in link lands. */
function SetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  // An emailed link carries a one-time token. It is spent only when the person
  // presses Continue, so a mail scanner that opens the link cannot use it up.
  const [tokenHash, setTokenHash] = useState<string | null>(() =>
    readLinkToken(),
  );
  const [verifying, setVerifying] = useState(false);

  async function continueWithLink() {
    if (!tokenHash) return;
    setVerifying(true);
    const { error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: 'recovery',
    });
    setVerifying(false);
    setTokenHash(null);
    if (verifyError) {
      setReady(false);
      return;
    }
    const rest = new URLSearchParams(window.location.search);
    rest.delete('token_hash');
    rest.delete('type');
    const query = rest.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`,
    );
    setReady(true);
  }

  useEffect(() => {
    if (tokenHash) return;
    // supabase-js reads the link's tokens from the URL on load.
    void supabase.auth
      .getSession()
      .then(({ data }) => setReady(Boolean(data.session)));
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) setReady(true);
    });
    return () => data.subscription.unsubscribe();
  }, [tokenHash]);

  const form = useForm({
    defaultValues: { password: '', confirm: '' },
    validators: { onSubmit: passwordSchema },
    onSubmit: async ({ value }) => {
      setError(null);
      const { error: updateError } = await supabase.auth.updateUser({
        password: value.password,
      });
      if (updateError) {
        setError(
          'Could not set the password. Ask for a new link and try again.',
        );
        return;
      }
      await navigate({ to: '/sign-in' });
    },
  });

  return (
    <AuthLayout>
      <AuthPanel>
        <AuthPanelHeader>
          <AuthPanelTitle>Set your password</AuthPanelTitle>
          <AuthPanelDescription>
            Then add an authenticator app. You need both to sign in.
          </AuthPanelDescription>
        </AuthPanelHeader>
        <div>
          {tokenHash ? (
            <Button
              type="button"
              className="w-full"
              disabled={verifying}
              onClick={() => void continueWithLink()}
            >
              Continue
            </Button>
          ) : ready === false ? (
            <Alert variant="warning">
              This link has expired or was used already. Ask for a new one from
              the sign-in page.
            </Alert>
          ) : (
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void form.handleSubmit();
              }}
            >
              <form.Field name="password">
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      value={field.state.value}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    <FieldError errors={field.state.meta.errors} />
                  </div>
                )}
              </form.Field>
              <form.Field name="confirm">
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="confirm-password">Again</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      value={field.state.value}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    <FieldError errors={field.state.meta.errors} />
                  </div>
                )}
              </form.Field>
              {error ? <Alert variant="destructive">{error}</Alert> : null}
              <Button type="submit" disabled={!ready}>
                Set password
              </Button>
            </form>
          )}
        </div>
      </AuthPanel>
    </AuthLayout>
  );
}
